import { useNavigate, useParams } from '@tanstack/react-router';
import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import { PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { releaseSaveBar } from '@nucleus-commerce/admin-kit/save-bar';
import type { SocialPost, SocialProvider } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Alert, AlertDescription, AlertTitle } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import { toast } from '@nucleus-commerce/ui/components/sonner';

import { useStoreDetail } from '@/features/stores';
import { ReadOnlyBanner } from '@/shared/store/read-only-banner';
import { displayTimeZone, useStore } from '@/shared/store/store-context';
import { useStoreEditable } from '@/shared/store/use-store-editable';

import {
  emptyPostForm,
  fromSocialPost,
  toCreatePostBody,
  toUpdatePostBody,
} from '../api/post-mappers';
import { PostForm } from '../components/post-form';
import { PostStatusBadge } from '../components/post-status-badge';
import { RemovePostDialog } from '../components/remove-post-dialog';
import {
  useCreateSocialPost,
  usePublishSocialPost,
  useUnscheduleSocialPost,
  useUpdateSocialPost,
} from '../hooks/use-post-mutations';
import { useSocialPost } from '../hooks/use-social-post';
import { displayPostTitle, isPostLocked } from '../schemas/post-state';

const EDIT_ROUTE = '/_authed/_active/s/$storeId/social/pinterest/posts/$postId' as const;
const PROVIDER: SocialProvider = 'pinterest';

const baseUrlOf = (primaryDomain: string | null | undefined) =>
  primaryDomain ? `https://${primaryDomain}` : null;

function usePublishToasts(storeId: string) {
  const { t } = useTranslation('social');
  const format = useFormat();
  const publish = usePublishSocialPost(storeId);
  const run = async (post: SocialPost, publishAt: string | undefined) => {
    const row = await publish.mutateAsync({
      id: post.id,
      body: publishAt ? { publishAt } : {},
    });
    toast.success(
      publishAt && row.publishAt
        ? t('toasts.postScheduled', { when: format.dateTime(row.publishAt) })
        : t('toasts.postQueued'),
    );
    return row;
  };
  return { run, isPending: publish.isPending };
}

function PostCreate() {
  const { t } = useTranslation('social');
  const store = useStore();
  const navigate = useNavigate();
  const detail = useStoreDetail(store.id);
  const { editable, reason } = useStoreEditable({ permission: 'social.write' });
  const create = useCreateSocialPost(store.id);
  const publish = usePublishToasts(store.id);

  const goToPost = (post: SocialPost) => {
    releaseSaveBar();
    return navigate({
      to: '/s/$storeId/social/pinterest/posts/$postId',
      params: { storeId: store.id, postId: post.id },
      replace: true,
    });
  };

  return (
    <PageContent>
      <PageHeader
        title={t('editor.createTitle')}
        description={t('editor.createDescription', { network: t(`${PROVIDER}.name`) })}
        backTo={{ to: '/s/$storeId/social/pinterest/posts', params: { storeId: store.id } }}
        backLabel={t('posts.title')}
      />
      <PostForm
        storeId={store.id}
        provider={PROVIDER}
        timeZone={displayTimeZone(store.timezone)}
        baseUrl={baseUrlOf(detail.data?.primaryDomain)}
        post={null}
        defaultValues={emptyPostForm()}
        editable={editable}
        readOnlyNotice={<ReadOnlyBanner reason={reason} />}
        saving={create.isPending}
        publishing={publish.isPending}
        onSubmit={(values) => create.mutateAsync(toCreatePostBody(values, PROVIDER))}
        onSaved={(post) => void goToPost(post)}
        onPublish={async (post, publishAt) => {
          await publish.run(post, publishAt);
          await goToPost(post);
        }}
      />
    </PageContent>
  );
}

function DangerZone({ storeId, post }: { storeId: string; post: SocialPost }) {
  const { t } = useTranslation('social');
  const navigate = useNavigate();
  const [removing, setRemoving] = useState<SocialPost | null>(null);
  const network = t(`${post.provider}.name`);
  return (
    <FormSection title={t('danger.title')} className="border-destructive/30">
      <p className="text-muted-foreground text-sm">
        {post.externalPostId
          ? t('danger.publishedDescription', { network })
          : t('danger.description')}
      </p>
      <Button
        type="button"
        variant="outline"
        className="text-destructive hover:text-destructive w-full"
        disabled={post.status === 'publishing'}
        onClick={() => setRemoving(post)}
      >
        <Trash2 aria-hidden />
        {t('actions.remove')}
      </Button>
      <RemovePostDialog
        storeId={storeId}
        row={removing}
        onOpenChange={(open) => {
          if (!open) setRemoving(null);
        }}
        onRemoved={() => {
          // The record is gone; a dirty form must not keep us on its page.
          releaseSaveBar();
          void navigate({ to: '/s/$storeId/social/pinterest/posts', params: { storeId } });
        }}
      />
    </FormSection>
  );
}

function LockedNotice({ post }: { post: SocialPost }) {
  const { t } = useTranslation('social');
  const network = t(`${post.provider}.name`);
  return (
    <Alert role="status" data-testid={`post-locked-${post.status}`}>
      <AlertTitle>{t(`editor.locked.${post.status}.title`, { network })}</AlertTitle>
      <AlertDescription>
        {t(`editor.locked.${post.status}.description`, { network })}
      </AlertDescription>
    </Alert>
  );
}

function PostEdit() {
  const { t } = useTranslation('social');
  const format = useFormat();
  const store = useStore();
  const { postId } = useParams({ from: EDIT_ROUTE });
  const detail = useStoreDetail(store.id);
  // Prefetched by the route loader, so this resolves from cache.
  const { data: post } = useSocialPost(store.id, postId);
  const storeEditable = useStoreEditable({ permission: 'social.write' });
  const update = useUpdateSocialPost(store.id);
  const publish = usePublishToasts(store.id);
  const unschedule = useUnscheduleSocialPost(store.id);
  if (!post) return null;
  const locked = isPostLocked(post.status);
  const editable = storeEditable.editable && !locked;
  const defaults = fromSocialPost(post);

  return (
    <PageContent>
      <PageHeader
        title={displayPostTitle(post)}
        meta={<PostStatusBadge status={post.status} />}
        description={
          <span title={format.utc(post.updatedAt)}>
            {t('editor.updatedAt', { when: format.relative(post.updatedAt) })}
          </span>
        }
        backTo={{ to: '/s/$storeId/social/pinterest/posts', params: { storeId: store.id } }}
        backLabel={t('posts.title')}
      />
      <PostForm
        key={post.updatedAt}
        storeId={store.id}
        provider={post.provider}
        timeZone={displayTimeZone(store.timezone)}
        baseUrl={baseUrlOf(detail.data?.primaryDomain)}
        post={post}
        defaultValues={defaults}
        editable={editable}
        readOnlyNotice={
          locked ? <LockedNotice post={post} /> : <ReadOnlyBanner reason={storeEditable.reason} />
        }
        saving={update.isPending}
        publishing={publish.isPending || unschedule.isPending}
        onSubmit={(values) =>
          update.mutateAsync({ id: post.id, body: toUpdatePostBody(values, defaults) })
        }
        onPublish={(saved, publishAt) => publish.run(saved, publishAt)}
        onUnschedule={() => unschedule.mutateAsync(post.id)}
        dangerZone={
          storeEditable.editable && post.status !== 'removed' ? (
            <DangerZone storeId={store.id} post={post} />
          ) : null
        }
      />
    </PageContent>
  );
}

export function PostCreatePage() {
  return (
    <FeatureErrorBoundary name="social.postCreate">
      <PostCreate />
    </FeatureErrorBoundary>
  );
}

export function PostEditPage() {
  return (
    <FeatureErrorBoundary name="social.postEdit">
      <PostEdit />
    </FeatureErrorBoundary>
  );
}
